
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
    AlarmSchedule,
    Form,
    FormField,
    PasswordSetup,
    Search
} from '@/components/business';
import {
    Button,
    ButtonGroup,
    Checkbox,
    Badge,
    CheckboxGroup,
    FileInput,
    IconButton,
    TransferList,
    Input,
    InputOTP,
    Label,
    RadioGroup,
    Schedule,
    Select,
    Slider,
    Switch,
    Text,
    TextArea
} from '@/components/ui';

import {
    CompactVariantPreview,
    createFamily,
    demoOptions,
    groupedOptions,
    LabeledPreviewCell,
    LabeledPreviewGrid,
    PreviewRow,
    PreviewStack,
    PreviewSurface,
    VariantGallery,
    VariantPreview,
    withStorySources
} from './helpers';

// The 7×3 matrix is sparse on purpose: (neutral, solid) is omitted because
// "high emphasis without semantic tone" is a contradiction.
const BUTTON_TONES = ['primary', 'secondary', 'destructive', 'info', 'success', 'warning', 'neutral'];
const BUTTON_APPEARANCES = ['solid', 'outline', 'ghost'];
const BUTTON_UNSUPPORTED_CELL = ['neutral', 'solid'];

const isUnsupportedCell = (tone, appearance) => (
    tone === BUTTON_UNSUPPORTED_CELL[0] && appearance === BUTTON_UNSUPPORTED_CELL[1]
);

const ButtonStory = () => (
    <PreviewStack>
        <section className="tw-space-y-3">
            <Text as="p" tone="muted" variant="overline">
                Tone × appearance
            </Text>
            <VariantPreview className="tw-items-stretch">
                <LabeledPreviewGrid columns={3}>
                    {BUTTON_TONES.flatMap(tone => (
                        BUTTON_APPEARANCES.map(appearance => (
                            <LabeledPreviewCell
                                key={`${tone}-${appearance}`}
                                label={`${tone} / ${appearance}`}
                                minHeight="112px"
                            >
                                {isUnsupportedCell(tone, appearance) ? (
                                    <Text tone="muted" variant="caption">not supported</Text>
                                ) : (
                                    <Button appearance={appearance} tone={tone}>Action</Button>
                                )}
                            </LabeledPreviewCell>
                        ))
                    ))}
                </LabeledPreviewGrid>
            </VariantPreview>
        </section>

        <section className="tw-space-y-3">
            <Text as="p" tone="muted" variant="overline">
                States and sizes
            </Text>
            <VariantPreview className="tw-items-stretch">
                <LabeledPreviewGrid minColumnWidth="124px">
                    <LabeledPreviewCell label="loading" minHeight="112px">
                        <Button loading>Saving</Button>
                    </LabeledPreviewCell>
                    <LabeledPreviewCell label="with icon" minHeight="112px">
                        <Button appearance="outline" icon="plus" tone="neutral">Add</Button>
                    </LabeledPreviewCell>
                    <LabeledPreviewCell label="small (32px)" minHeight="112px">
                        <Button size="small">Action</Button>
                    </LabeledPreviewCell>
                    <LabeledPreviewCell label="medium (40px)" minHeight="112px">
                        <Button>Action</Button>
                    </LabeledPreviewCell>
                    <LabeledPreviewCell label="large (44px)" minHeight="112px">
                        <Button size="large">Action</Button>
                    </LabeledPreviewCell>
                </LabeledPreviewGrid>
            </VariantPreview>
        </section>

        <section className="tw-space-y-3">
            <Text as="p" tone="muted" variant="overline">
                Icon position and alignment
            </Text>
            <VariantPreview className="tw-items-stretch">
                <LabeledPreviewGrid minColumnWidth="240px">
                    <LabeledPreviewCell label="iconPosition trailing / iconAlign edge" minHeight="112px">
                        <Button
                            appearance="outline"
                            fullWidth
                            icon="chevronRight"
                            iconAlign="edge"
                            iconPosition="trailing"
                            tone="primary"
                        >
                            Select all (12)
                        </Button>
                    </LabeledPreviewCell>
                    <LabeledPreviewCell label="iconPosition leading / iconAlign edge" minHeight="112px">
                        <Button
                            appearance="outline"
                            fullWidth
                            icon="chevronLeft"
                            iconAlign="edge"
                            iconPosition="leading"
                            tone="destructive"
                        >
                            Remove all
                        </Button>
                    </LabeledPreviewCell>
                </LabeledPreviewGrid>
            </VariantPreview>
        </section>
    </PreviewStack>
);

const ButtonHero = () => (
    <Button loading size="large" tone="info">Click me</Button>
);

const ButtonGroupHero = () => (
    <ButtonGroup>
        <Button appearance="outline" tone="neutral">Day</Button>
        <Button appearance="outline" tone="neutral">Week</Button>
        <Button appearance="outline" tone="neutral">Month</Button>
    </ButtonGroup>
);

const IconButtonStory = () => (
    <PreviewStack>
        <section className="tw-space-y-3">
            <Text as="p" tone="muted" variant="overline">
                Sizes
            </Text>
            <VariantPreview className="tw-items-stretch">
                <LabeledPreviewGrid minColumnWidth="120px">
                    {['tiny', 'small', 'medium', 'large'].map(size => (
                        <LabeledPreviewCell key={size} label={size} minHeight="80px">
                            <IconButton aria-label="Close" icon="close" size={size} />
                        </LabeledPreviewCell>
                    ))}
                </LabeledPreviewGrid>
            </VariantPreview>
        </section>
        <section className="tw-space-y-3">
            <Text as="p" tone="muted" variant="overline">
                Tone × appearance
            </Text>
            <VariantPreview className="tw-items-stretch">
                <LabeledPreviewGrid columns={3}>
                    {BUTTON_TONES.flatMap(tone => (
                        BUTTON_APPEARANCES.map(appearance => (
                            <LabeledPreviewCell
                                key={`${tone}-${appearance}`}
                                label={`${tone} / ${appearance}`}
                                minHeight="80px"
                            >
                                {isUnsupportedCell(tone, appearance) ? (
                                    <Text tone="muted" variant="caption">not supported</Text>
                                ) : (
                                    <IconButton aria-label="Close" appearance={appearance} icon="close" tone={tone} />
                                )}
                            </LabeledPreviewCell>
                        ))
                    ))}
                </LabeledPreviewGrid>
            </VariantPreview>
        </section>
    </PreviewStack>
);

const IconButtonHero = () => (
    <IconButton aria-label="Close" icon="close" />
);

const ButtonGroupStory = () => (
    <VariantGallery className="xl:tw-grid-cols-2">
        <CompactVariantPreview meta={[{ label: 'orientation', value: 'horizontal' }, { label: 'children', value: 'Button' }]}>
            <PreviewRow>
                <ButtonGroup>
                    <Button appearance="outline" tone="neutral">Day</Button>
                    <Button appearance="outline" tone="neutral">Week</Button>
                    <Button appearance="outline" tone="neutral">Month</Button>
                </ButtonGroup>
            </PreviewRow>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'orientation', value: 'vertical' }, { label: 'children', value: 'Button' }]} className="tw-items-start">
            <PreviewRow>
                <ButtonGroup orientation="vertical">
                    <Button appearance="outline" tone="neutral">Draft</Button>
                    <Button appearance="outline" tone="neutral">Review</Button>
                    <Button appearance="outline" tone="neutral">Publish</Button>
                </ButtonGroup>
            </PreviewRow>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'orientation', value: 'horizontal' }, { label: 'children', value: 'Button + icon' }]}>
            <PreviewRow>
                <ButtonGroup>
                    <Button appearance="outline" icon="chartColumn" tone="neutral">Bars</Button>
                    <Button appearance="outline" icon="chartLine" tone="neutral">Line</Button>
                    <Button appearance="outline" icon="chartPie" tone="neutral">Pie</Button>
                </ButtonGroup>
            </PreviewRow>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'orientation', value: 'horizontal' }, { label: 'children', value: 'IconButton' }]}>
            <PreviewRow>
                <ButtonGroup>
                    <IconButton appearance="outline" aria-label="Bars" icon="chartColumn" tone="neutral" />
                    <IconButton appearance="outline" aria-label="Line" icon="chartLine" tone="neutral" />
                    <IconButton appearance="outline" aria-label="Pie" icon="chartPie" tone="neutral" />
                </ButtonGroup>
            </PreviewRow>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'orientation', value: 'horizontal' }, { label: 'children', value: 'IconButton + Button' }]}>
            <PreviewRow>
                <ButtonGroup>
                    <IconButton appearance="outline" aria-label="Previous period" icon="chevronLeft" tone="neutral" />
                    <Button appearance="outline" tone="neutral">This week</Button>
                    <IconButton appearance="outline" aria-label="Next period" icon="chevronRight" tone="neutral" />
                </ButtonGroup>
            </PreviewRow>
        </CompactVariantPreview>
    </VariantGallery>
);

const CheckboxStory = () => {
    const [checked, setChecked] = React.useState(true);

    return (
        <VariantGallery className="xl:tw-grid-cols-2">
            <CompactVariantPreview meta={[{ label: 'state', value: checked ? 'checked' : 'unchecked' }]} className="tw-items-start">
                <PreviewRow>
                    <Checkbox checked={checked} id="checkbox-preview" label="Receive updates" onCheckedChange={setChecked} />
                </PreviewRow>
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'state', value: 'disabled' }]} className="tw-items-start">
                <PreviewRow>
                    <Checkbox disabled id="checkbox-disabled" label="Disabled option" />
                </PreviewRow>
            </CompactVariantPreview>
        </VariantGallery>
    );
};

const CheckboxHero = () => {
    const [checked, setChecked] = React.useState(true);

    return (
        <Checkbox checked={checked} id="checkbox-hero" label="Receive updates" onCheckedChange={setChecked} />
    );
};

const CheckboxGroupHero = () => {
    const [values, setValues] = React.useState(['alerts', 'weekly']);

    return (
        <CheckboxGroup
            onChange={setValues}
            options={[
                { value: 'alerts', label: 'Instant alerts' },
                { value: 'digest', label: 'Daily digest', description: 'One email each morning summarising overnight activity.' },
                { value: 'weekly', label: 'Weekly summary' },
                { value: 'beta', label: 'Beta features', description: 'Preview channel.', disabled: true }
            ]}
            values={values}
        />
    );
};

const TransferListHero = () => {
    const options = Array.from({ length: 8 }, (_, index) => ({
        label: `Component ${index + 1}`,
        value: String(index + 1)
    }));
    const [values, setValues] = React.useState(['2', '5']);

    return (
        <TransferList
            emptyMessage="No matching components"
            limit={5}
            onChange={setValues}
            options={options}
            searchPlaceholder="Search components"
            values={values}
        />
    );
};

const FormHero = () => {
    // Everything is inlined so the displayed source is self-contained:
    // schema, option lists, defaults, conditional logic, submit handler.
    // The build script only extracts the function body, so module-level
    // helpers would render as undeclared identifiers in the code block.

    const inviteSchema = z.object({
        name: z.string().min(1, 'Name is required.'),
        email: z.string().email('Enter a valid email address.'),
        role: z.enum(['admin', 'member', 'viewer'], {
            errorMap: () => ({ message: 'Pick a role.' })
        }),
        department: z.string().min(1, 'Pick a department.'),
        defaultDashboard: z.string(),
        receiveNotifications: z.boolean(),
        notes: z.string().max(280, 'Notes must be 280 characters or fewer.')
    }).refine(
        data => data.role !== 'viewer' || data.defaultDashboard.length > 0,
        { message: 'Pick a default dashboard.', path: ['defaultDashboard'] }
    );

    const roleOptions = [
        { value: 'admin', label: 'Admin', description: 'Full access — manage users and settings.' },
        { value: 'member', label: 'Member', description: 'Read and write across the org.' },
        { value: 'viewer', label: 'Viewer', description: 'Read-only access.' }
    ];
    const departmentOptions = [
        { value: 'engineering', label: 'Engineering' },
        { value: 'product', label: 'Product' },
        { value: 'design', label: 'Design' },
        { value: 'operations', label: 'Operations' },
        { value: 'sales', label: 'Sales' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'finance', label: 'Finance' },
        { value: 'people', label: 'People' }
    ];
    const dashboardOptions = [
        { value: 'overview', label: 'Overview' },
        { value: 'energy', label: 'Energy' },
        { value: 'alarms', label: 'Alarms' }
    ];

    const form = useForm({
        mode: 'onChange',
        resolver: zodResolver(inviteSchema),
        defaultValues: {
            name: '',
            email: '',
            role: '',
            department: '',
            defaultDashboard: '',
            receiveNotifications: true,
            notes: ''
        }
    });
    const role = form.watch('role');
    const dashboardLocked = role !== 'viewer';

    const onSubmit = data => {
        alert(`Submitted\n\n${JSON.stringify(data, null, 2)}`);
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormField name="name" label="Name" required
                render={({ field }) => <Input {...field} />}
            />
            <FormField name="email" label="Email" required
                info="Invites and notifications are sent here."
                render={({ field }) => <Input {...field} type="email" />}
            />
            <FormField name="role" label="Role" required
                render={({ field }) => (
                    <RadioGroup
                        onValueChange={value => {
                            field.onChange(value);
                            if (value !== 'viewer') {
                                form.setValue('defaultDashboard', '', { shouldValidate: true });
                            }
                        }}
                        options={roleOptions}
                        value={field.value}
                    />
                )}
            />
            <FormField name="department" label="Department" required
                render={({ field }) => (
                    <Select
                        onChange={selected => field.onChange(selected?.value ?? '')}
                        options={departmentOptions}
                        placeholder="Search and pick"
                        search
                        value={departmentOptions.find(option => option.value === field.value) ?? null}
                    />
                )}
            />
            <FormField name="defaultDashboard" label="Default dashboard"
                disabled={dashboardLocked}
                info={dashboardLocked
                    ? 'Available only when role is Viewer.'
                    : 'Shown on first login.'}
                render={({ field }) => (
                    <Select
                        isDisabled={dashboardLocked}
                        onChange={selected => field.onChange(selected?.value ?? '')}
                        options={dashboardOptions}
                        placeholder="Pick a dashboard"
                        value={dashboardOptions.find(option => option.value === field.value) ?? null}
                    />
                )}
            />
            <FormField name="receiveNotifications"
                render={({ field }) => (
                    <Switch
                        checked={field.value}
                        label="Receive email notifications"
                        onCheckedChange={field.onChange}
                    />
                )}
            />
            <FormField name="notes" label="Notes"
                info="Up to 280 characters. Visible to admins."
                render={({ field }) => <TextArea {...field} />}
            />
            <div className="tw-flex tw-items-end tw-justify-end">
                <Button type="submit" disabled={!form.formState.isValid}>Submit</Button>
            </div>
        </Form>
    );
};

const InputHero = () => (
    <Input defaultValue="Regular input" />
);

const InputStory = () => (
    <VariantGallery>
        <CompactVariantPreview meta={[{ label: 'state', value: 'default' }]}>
            <Input defaultValue="Regular input" />
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'state', value: 'disabled' }]}>
            <Input disabled defaultValue="Disabled input" />
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'state', value: 'placeholder' }]}>
            <Input placeholder="Placeholder value" />
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'size', value: 'small' }]}>
            <Input defaultValue="Small input" size="small" />
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'type', value: 'search' }]}>
            <Input defaultValue="react" type="search" />
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'type', value: 'password' }]}>
            <Input defaultValue="WiseMetering123!" type="password" />
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'type', value: 'date' }]}>
            <Input defaultValue="2026-05-15" type="date" />
        </CompactVariantPreview>
    </VariantGallery>
);

const InputOTPHero = () => {
    const [value, setValue] = React.useState('12');

    return <InputOTP length={4} value={value} onChange={setValue}/>;
};

const FileInputHero = () => {
    const [value, setValue] = React.useState(null);

    return (
        <div className="tw-w-80">
            <FileInput onChange={setValue} value={value}/>
        </div>
    );
};

const FileInputStory = () => {
    // FileInput is fully controlled (the native file input can't be set
    // programmatically), so every cell needs its own state to demonstrate
    // real behaviour — clearing, switching files, etc. A shared mock File
    // seeds the cells that start populated; from there the visitor can
    // clear and re-pick to feel the interaction.
    const sampleFile = React.useMemo(
        () => new File(['hello'], 'metals-2026-q1.xlsx', { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        []
    );

    const [emptyValue, setEmptyValue] = React.useState(null);
    const [withFileValue, setWithFileValue] = React.useState(sampleFile);
    const [disabledValue, setDisabledValue] = React.useState(sampleFile);
    const [nonClearableValue, setNonClearableValue] = React.useState(sampleFile);
    const [smallValue, setSmallValue] = React.useState(sampleFile);
    const [acceptValue, setAcceptValue] = React.useState(null);

    return (
        <VariantGallery>
            <CompactVariantPreview meta={[{ label: 'state', value: 'empty' }]}>
                <FileInput onChange={setEmptyValue} value={emptyValue}/>
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'state', value: 'with file' }]}>
                <FileInput onChange={setWithFileValue} value={withFileValue}/>
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'state', value: 'disabled' }]}>
                <FileInput disabled onChange={setDisabledValue} value={disabledValue}/>
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'clearable', value: 'false' }]}>
                <FileInput clearable={false} onChange={setNonClearableValue} value={nonClearableValue}/>
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'size', value: 'small' }]}>
                <FileInput onChange={setSmallValue} size="small" value={smallValue}/>
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'accept', value: '.xlsx,.csv' }]}>
                <FileInput accept=".xlsx,.csv" onChange={setAcceptValue} value={acceptValue}/>
            </CompactVariantPreview>
        </VariantGallery>
    );
};

const LabelHero = () => (
    <div className="tw-grid tw-gap-2">
        <Label htmlFor="label-hero">Email address</Label>
        <Input id="label-hero" defaultValue="alex@wisemetering.com" />
    </div>
);

const LabelStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'state', value: 'default' }]}>
            <div className="tw-grid tw-gap-2">
                <Label htmlFor="label-demo">Email address</Label>
                <Input id="label-demo" defaultValue="alex@wisemetering.com" />
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'state', value: 'required' }]}>
            <div className="tw-grid tw-gap-2">
                <Label htmlFor="label-required" required>Email address</Label>
                <Input id="label-required" placeholder="you@company.com" />
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'state', value: 'disabled' }]}>
            <div className="tw-grid tw-gap-2">
                <Label htmlFor="label-disabled">Disabled field</Label>
                <Input id="label-disabled" disabled defaultValue="Read only" />
            </div>
        </VariantPreview>
    </PreviewStack>
);

const PasswordSetupHero = () => {
    const [{ password, isValid }, setState] = React.useState({ password: '', isValid: false });

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <PasswordSetup onChange={setState} />
            <Button
                disabled={!isValid}
                fullWidth
                onClick={() => alert(`Submitting: ${password}`)}
                type="button"
            >
                Submit
            </Button>
        </div>
    );
};

const SearchHero = () => {
    const dataset = [
        { value: 'lisbon-electricity', label: 'Lisbon HQ · Electricity', kind: 'Active energy', site: 'Lisbon HQ', icon: 'bolt' },
        { value: 'lisbon-water', label: 'Lisbon HQ · Water', kind: 'Water', site: 'Lisbon HQ', icon: 'water' },
        { value: 'lisbon-gas', label: 'Lisbon HQ · Gas', kind: 'Gas volume', site: 'Lisbon HQ', icon: 'gas' },
        { value: 'porto-electricity', label: 'Porto Office · Electricity', kind: 'Active energy', site: 'Porto Office', icon: 'bolt' },
        { value: 'porto-cooling', label: 'Porto Office · Cooling', kind: 'Cooled water energy', site: 'Porto Office', icon: 'cooling' },
        { value: 'madrid-heating', label: 'Madrid Branch · Heating', kind: 'Hot water energy', site: 'Madrid Branch', icon: 'heating' },
        { value: 'madrid-solar', label: 'Madrid Branch · Solar', kind: 'Solar power', site: 'Madrid Branch', icon: 'sun' },
        { value: 'barcelona-co2', label: 'Barcelona Site · CO₂', kind: 'Carbon dioxide', site: 'Barcelona Site', icon: 'carbonDioxide' }
    ];
    const [query, setQuery] = React.useState('');
    const normalized = query.trim().toLowerCase();
    const items = normalized.length >= 2
        ? dataset
            .filter(entry => entry.label.toLowerCase().includes(normalized) || entry.kind.toLowerCase().includes(normalized))
            .map(entry => ({
                value: entry.value,
                label: entry.label,
                description: [entry.site, entry.kind].join(' · '),
                leadingIcon: entry.icon,
                trailingIcon: 'chevronRight'
            }))
        : [];
    const summary = normalized.length >= 2
        ? `${items.length} ${items.length === 1 ? 'result' : 'results'}`
        : null;

    return (
        <Search
            items={items}
            maxResults={2}
            minQueryLength={2}
            onSelect={value => alert(`Picked: ${value}`)}
            onValueChange={setQuery}
            placeholder="Search indicators…"
            size="small"
            summary={summary}
            value={query}
        />
    );
};

const RadioGroupHero = () => {
    const [value, setValue] = React.useState('stable');

    return (
        <RadioGroup
            onValueChange={setValue}
            options={[
                { value: 'stable', label: 'Stable', description: 'The default channel — broadly tested.' },
                { value: 'beta', label: 'Beta', description: 'Newer features, occasional rough edges.' },
                { value: 'deprecated', label: 'Deprecated', disabled: true }
            ]}
            value={value}
        />
    );
};

const ScheduleHero = () => {
    const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday'];
    const [schedule, setSchedule] = React.useState({});

    const summarizeDay = slots => {
        if (!slots?.length) {
            return '—';
        }
        const ranges = [];
        let start = null;
        for (let i = 0; i <= slots.length; i += 1) {
            if (slots[i]) {
                if (start === null) {
                    start = i;
                }
            } else if (start !== null) {
                ranges.push(start === i - 1 ? `${start}` : `${start}–${i - 1}`);
                start = null;
            }
        }
        return ranges.length === 0 ? '—' : ranges.join(', ');
    };

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-4">
            <Schedule onChange={setSchedule} value={schedule} />
            <div className="tw-flex tw-flex-col tw-gap-1">
                {DAYS.map(day => (
                    <Text key={day} tone="muted" variant="body">
                        {day.padEnd(10)}{' '}
                        <Text as="span" tone="foreground" variant="control">
                            {summarizeDay(schedule[day])}
                        </Text>
                    </Text>
                ))}
            </div>
        </div>
    );
};

const ScheduleReadonlyHero = () => {
    const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday'];
    // 9am–6pm window every day (slots 36..71 inclusive of 96 quarters).
    const businessHours = Array.from({ length: 96 }, (_, i) => i >= 36 && i < 72);
    const value = Object.fromEntries(DAYS.map(day => [day, businessHours]));

    return <Schedule readonly value={value} />;
};

// Build a string-shape schedule for AlarmSchedule (backend wire format).
const buildDemoAlarmSchedule = () => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday'];
    const ON_START = 9 * 4;
    const ON_END = 18 * 4;

    return Object.fromEntries(
        days.map(day => [
            day,
            Array.from({ length: 24 * 4 }, (_, slot) => (slot >= ON_START && slot < ON_END ? 'on' : 'off'))
        ])
    );
};

const DEMO_ALARM_SCHEDULE = buildDemoAlarmSchedule();

const AlarmScheduleHero = () => {
    const [schedule, setSchedule] = React.useState(DEMO_ALARM_SCHEDULE);
    const [minValue, setMinValue] = React.useState(0);
    const [maxValue, setMaxValue] = React.useState(1000);

    return (
        <AlarmSchedule
            maxValue={maxValue}
            minValue={minValue}
            onMaxValueChange={setMaxValue}
            onMinValueChange={setMinValue}
            onScheduleChange={setSchedule}
            schedule={schedule}
            unit="kWh"
        />
    );
};

const AlarmScheduleReadonlyHero = () => (
    <AlarmSchedule
        maxValue={1000}
        minValue={0}
        schedule={DEMO_ALARM_SCHEDULE}
        unit="kWh"
    />
);

const SelectHero = () => (
    <Select
        clearable
        defaultValue={demoOptions[1]}
        isMulti
        options={demoOptions}
        search
    />
);

const SelectStory = () => {
    const optionByValue = React.useMemo(() => Object.fromEntries(demoOptions.map(option => [option.value, option])), []);
    const variants = [
        {
            id: 'default',
            meta: [{ label: 'variant', value: 'default' }],
            props: {
                defaultValue: optionByValue.alpha,
                options: demoOptions
            }
        },
        {
            id: 'clearable',
            meta: [{ label: 'clearable', value: 'true' }],
            props: {
                clearable: true,
                defaultValue: optionByValue.bravo,
                options: demoOptions
            }
        },
        {
            id: 'search',
            meta: [{ label: 'search', value: 'true' }],
            props: {
                defaultValue: optionByValue.charlie,
                options: demoOptions,
                search: true
            }
        },
        {
            id: 'multi',
            meta: [{ label: 'isMulti', value: 'true' }],
            props: {
                defaultValue: [optionByValue.alpha, optionByValue.delta],
                isMulti: true,
                options: demoOptions
            }
        },
        {
            id: 'multi-clearable',
            meta: [{ label: 'isMulti', value: 'true' }, { label: 'clearable', value: 'true' }],
            props: {
                clearable: true,
                defaultValue: [optionByValue.alpha, optionByValue.charlie],
                isMulti: true,
                options: demoOptions
            }
        },
        {
            id: 'multi-search',
            meta: [{ label: 'isMulti', value: 'true' }, { label: 'search', value: 'true' }],
            props: {
                defaultValue: [optionByValue.bravo, optionByValue.delta],
                isMulti: true,
                options: demoOptions,
                search: true
            }
        },
        {
            id: 'multi-clearable-search',
            meta: [{ label: 'isMulti', value: 'true' }, { label: 'clearable', value: 'true' }, { label: 'search', value: 'true' }],
            props: {
                clearable: true,
                defaultValue: [optionByValue.bravo, optionByValue.echo],
                isMulti: true,
                options: demoOptions,
                search: true
            }
        },
        {
            id: 'grouped-search-sort',
            meta: [{ label: 'grouped', value: 'true' }, { label: 'search', value: 'true' }, { label: 'sort', value: 'asc' }],
            props: {
                defaultValue: optionByValue.alpha,
                options: groupedOptions,
                search: true,
                sort: 'asc'
            }
        },
        {
            id: 'small',
            meta: [{ label: 'size', value: 'small' }],
            props: {
                defaultValue: optionByValue.alpha,
                options: demoOptions,
                size: 'small'
            }
        }
    ];

    return (
        <PreviewStack>
            {variants.map(variant => (
                <VariantPreview key={variant.id} meta={variant.meta}>
                    <Select {...variant.props} />
                </VariantPreview>
            ))}
        </PreviewStack>
    );
};

const SliderHero = () => {
    const [primary, setPrimary] = React.useState(40);
    const [secondary, setSecondary] = React.useState(70);

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-6">
            <div className="tw-flex tw-items-center tw-gap-4">
                <div className="tw-flex-1">
                    <Slider onChange={setPrimary} value={primary} />
                </div>
                <Text tone="muted" variant="caption">{primary}</Text>
            </div>
            <div className="tw-flex tw-items-center tw-gap-4">
                <div className="tw-flex-1">
                    <Slider onChange={setSecondary} tone="secondary" value={secondary} />
                </div>
                <Text tone="muted" variant="caption">{secondary}</Text>
            </div>
        </div>
    );
};

const SwitchHero = () => {
    const [enabled, setEnabled] = React.useState(true);

    return (
        <Switch checked={enabled} description="Secondary helper text" label="Default switch" onCheckedChange={setEnabled} />
    );
};

const SwitchStory = () => {
    const [enabled, setEnabled] = React.useState(true);

    return (
        <VariantGallery columns={2}>
            <CompactVariantPreview meta={[{ label: 'tone', value: 'neutral' }, { label: 'checked', value: String(enabled) }]} className="tw-items-start">
                <Switch checked={enabled} description="Secondary helper text" label="Default switch" onCheckedChange={setEnabled} />
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'tone', value: 'info' }, { label: 'checked', value: 'true' }]} className="tw-items-start">
                <Switch defaultChecked label="Info switch" tone="info" />
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'tone', value: 'success' }, { label: 'checked', value: 'true' }]} className="tw-items-start">
                <Switch defaultChecked label="Success switch" tone="success" />
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'tone', value: 'warning' }, { label: 'checked', value: 'true' }]} className="tw-items-start">
                <Switch defaultChecked label="Warning switch" tone="warning" />
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'tone', value: 'destructive' }, { label: 'checked', value: 'true' }]} className="tw-items-start">
                <Switch defaultChecked label="Destructive switch" tone="destructive" />
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'size', value: 'small' }, { label: 'tone', value: 'success' }]} className="tw-items-start">
                <Switch defaultChecked label="Small switch" size="small" tone="success" />
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'state', value: 'disabled (off)' }]} className="tw-items-start">
                <Switch disabled label="Disabled switch" description="Cannot be toggled right now." />
            </CompactVariantPreview>
            <CompactVariantPreview meta={[{ label: 'state', value: 'disabled (on)' }]} className="tw-items-start">
                <Switch disabled defaultChecked label="Disabled (checked)" tone="info" />
            </CompactVariantPreview>
        </VariantGallery>
    );
};

const TextAreaHero = () => (
    <TextArea defaultValue="Longer free-form text for notes and comments." />
);

const TextAreaStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'state', value: 'default' }]}>
            <TextArea defaultValue="Longer free-form text for notes and comments." />
        </VariantPreview>
        <VariantPreview meta={[{ label: 'state', value: 'disabled' }]}>
            <TextArea disabled defaultValue="Disabled textarea" />
        </VariantPreview>
        <VariantPreview meta={[{ label: 'size', value: 'small' }]}>
            <TextArea defaultValue="Small textarea — smaller typography, same content-driven height." size="small" />
        </VariantPreview>
    </PreviewStack>
);

const formsStorySections = [
    createFamily({
        id: 'button',
        label: 'Button',
        description: 'Primary button wrapper with loading, variant and size support.',
        subcomponents: ['Button'],
        stories: [{ id: 'button-variants', title: 'Variants and sizes', render: ButtonStory, hero: ButtonHero }]
    }),
    createFamily({
        id: 'button-group',
        label: 'ButtonGroup',
        description: 'Groups adjacent buttons as a single segmented control.',
        subcomponents: ['ButtonGroup'],
        stories: [{ id: 'button-group-orientations', title: 'Horizontal and vertical groups', render: ButtonGroupStory, hero: ButtonGroupHero }]
    }),
    createFamily({
        id: 'icon-button',
        label: 'IconButton',
        description: 'Square hit target around a single icon. Use for chrome-only actions where the affordance is icon plus aria-label.',
        subcomponents: ['IconButton'],
        stories: [{ id: 'icon-button-variants', title: 'Sizes and variants', render: IconButtonStory, hero: IconButtonHero }]
    }),
    createFamily({
        id: 'checkbox',
        label: 'Checkbox',
        description: 'Checkbox with optional label prop and disabled state.',
        subcomponents: ['Checkbox'],
        stories: [{ id: 'checkbox-basic', title: 'Checkbox states', render: CheckboxStory, hero: CheckboxHero }]
    }),
    createFamily({
        id: 'checkbox-group',
        label: 'CheckboxGroup',
        description: 'Stacked group helper built on top of Checkbox.',
        subcomponents: ['CheckboxGroup'],
        stories: [{ id: 'checkbox-group-basic', title: 'Grouped checkboxes', hero: CheckboxGroupHero }]
    }),
    createFamily({
        id: 'transfer-list',
        label: 'TransferList',
        description: 'Two-column transfer list with search and bulk actions.',
        subcomponents: ['TransferList'],
        stories: [{ id: 'transfer-list-basic', title: 'Transfer list', hero: TransferListHero }]
    }),
    createFamily({
        id: 'form',
        label: 'Form',
        layer: 'business',
        description: 'Closed scaffolding around react-hook-form + zod. `<Form form={form} onSubmit={...}>` wraps `<FormProvider>` and stacks children vertically. `<FormField name label required? info? disabled? grow? render>` subscribes to RHF, renders Label + control + inline error + optional info, and delivers the field wiring through a render prop (controls have inconsistent value/change prop shapes — Input vs Checkbox vs Select — so the caller adapts). Validation lives at the form level: declare the schema with zod, wire it via `useForm({ resolver: zodResolver(schema), mode: \'onChange\' })`, and FormField surfaces whatever error RHF produces for `name` — no per-field rules. The `useDirtyValues(form)` hook is the canonical helper for Edit screens: it returns a function that builds a PATCH payload containing only the fields the user actually touched, so save calls never ship the full form snapshot.',
        subcomponents: ['Form', 'FormField', 'useDirtyValues'],
        stories: [
            { id: 'form-basic', title: 'RHF + zod', hero: FormHero }
        ]
    }),
    createFamily({
        id: 'input',
        label: 'Input',
        description: 'Base text input used across the UI kit.',
        subcomponents: ['Input'],
        stories: [{ id: 'input-states', title: 'Input states', render: InputStory, hero: InputHero }]
    }),
    createFamily({
        id: 'file-input',
        label: 'FileInput',
        description: 'File picker primitive. Hidden native <input type="file"> behind a Button trigger, with a filename slot and an inline clear. `value` is the selected `File` (or `null`); `onChange(file)` fires with the File or `null` — no synthetic event wrapping, so RHF Controllers can forward `field.onChange` straight through.',
        subcomponents: ['FileInput'],
        stories: [{ id: 'file-input-states', title: 'States and sizes', render: FileInputStory, hero: FileInputHero }]
    }),
    createFamily({
        id: 'input-otp',
        label: 'InputOTP',
        description: 'Segmented input for codes and one-time passwords.',
        subcomponents: ['InputOTP'],
        stories: [{ id: 'input-otp-basic', title: 'OTP entry', hero: InputOTPHero }]
    }),
    createFamily({
        id: 'label',
        label: 'Label',
        description: 'Accessible label primitive for field associations.',
        subcomponents: ['Label'],
        stories: [{ id: 'label-basic', title: 'Label usage', render: LabelStory, hero: LabelHero }]
    }),
    createFamily({
        id: 'password-setup',
        label: 'PasswordSetup',
        layer: 'business',
        description: 'Self-contained widget for choosing a new password — owns the inputs, runs the requirements checklist, and generates a strong password on demand.',
        subcomponents: ['PasswordSetup'],
        stories: [{ id: 'password-setup-basic', title: 'New-password widget', hero: PasswordSetupHero }]
    }),
    createFamily({
        id: 'radio-group',
        label: 'RadioGroup',
        description: 'Single-choice radio selection built on Radix primitives.',
        subcomponents: ['RadioGroup'],
        stories: [{ id: 'radio-group-basic', title: 'Single selection', hero: RadioGroupHero }]
    }),
    createFamily({
        id: 'alarm-schedule',
        label: 'AlarmSchedule',
        layer: 'business',
        description: 'Alarm-domain composition around <Schedule>: adds min/max value inputs, unit display, bulk-edit "Change?" toggles per section, and the baseline-alarm overlay. Owns the conversion between the backend\'s `\'on\'/\'off\'` schedule strings and the boolean shape that <Schedule> expects.',
        subcomponents: ['AlarmSchedule'],
        stories: [
            { id: 'alarm-schedule-editable', title: 'Editable with values', hero: AlarmScheduleHero },
            { id: 'alarm-schedule-readonly', title: 'Read-only', hero: AlarmScheduleReadonlyHero }
        ]
    }),
    createFamily({
        id: 'schedule',
        label: 'Schedule',
        description: 'Closed weekly schedule grid — 8 day rows × 24 hour rows × 4 quarter-hour slots per cell. Pure paint + drag interaction; no labels, no inputs, no overlays. value is Record<day, boolean[96]>. Drag direction auto-determined from the initial cell (Google Calendar-style). `readonly` flips to display-only.',
        subcomponents: ['Schedule'],
        stories: [
            { id: 'schedule-editable', title: 'Editable grid', hero: ScheduleHero },
            { id: 'schedule-readonly', title: 'Read-only', hero: ScheduleReadonlyHero }
        ]
    }),
    createFamily({
        id: 'search',
        label: 'Search',
        layer: 'business',
        description: 'Typeahead pattern over an Input — composes Input + Popover + List into a single search-to-pick interaction. Caller owns the query state and provides the filtered `items`; Search owns the open / close state of the results popover. The popover always paints as wide as the input.',
        subcomponents: ['Search'],
        stories: [{ id: 'search-basic', title: 'Search with filtered results', hero: SearchHero }]
    }),
    createFamily({
        id: 'select',
        label: 'Select',
        description: 'Custom select wrapper with clearable, searchable, grouped and multi-select variants.',
        subcomponents: ['Select'],
        stories: [{ id: 'select-variants', title: 'Curated variants', render: SelectStory, hero: SelectHero }]
    }),
    createFamily({
        id: 'slider',
        label: 'Slider',
        description: 'Single-thumb slider for numeric values.',
        subcomponents: ['Slider'],
        stories: [{ id: 'slider-basic', title: 'Slider value', hero: SliderHero }]
    }),
    createFamily({
        id: 'switch',
        label: 'Switch',
        description: 'Labeled switch with size and variant support.',
        subcomponents: ['Switch'],
        stories: [{ id: 'switch-variants', title: 'Switch variants', render: SwitchStory, hero: SwitchHero }]
    }),
    createFamily({
        id: 'text-area',
        label: 'TextArea',
        description: 'Multiline text input for notes and long-form content.',
        subcomponents: ['TextArea'],
        stories: [{ id: 'text-area-basic', title: 'Textarea states', render: TextAreaStory, hero: TextAreaHero }]
    })
].map(withStorySources);

export { formsStorySections };
